/**
 * More info at: https://www.notion.so/vtexhandbook/Event-API-Documentation-48eee26730cf4d7f80f8fd7262231f84
 */
// import type { AnalyticsEvent } from '@faststore/sdk'

import { getStoreConfig } from 'src/store-config/get-config'
import { axiosClient } from 'src/services/axiosInstance'
import { getCookie, setCookie } from 'src/utils/cookies'

import type {
  SearchAutocomplete,
  SearchClickEvent,
  SearchEventQuery,
} from '../types'

const THIRTY_MINUTES = 30
const ONE_YEAR_IN_MINUTES = 365 * 24 * 60

const randomUUID = () =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : (Math.random() * 1e6).toFixed(0)

const createStorage = (key: string, expiresInMinutes: number) => {
  return () => {
    const cookieName = key

    let payload = getCookie(cookieName)

    if (!payload) {
      payload = randomUUID()
      setCookie(cookieName, payload, { expiresInMinutes })
    }

    return payload
  }
}

const user = {
  anonymous: createStorage('vtex.search.anonymous', ONE_YEAR_IN_MINUTES),
  session: createStorage('vtex.search.session', THIRTY_MINUTES),
}

type SearchEvent =
  | {
      type: 'session.ping'
    }
  | {
      position: number
      productId: string
      text: string
      url: string
      type: 'search.click'
    }
  | {
      type: 'search.query'
      text: string
      misspelled: boolean
      match: number
      operator: 'and' | 'or'
      locale: string
      session?: string
      anonymous?: string
    }
  | {
      type: 'search.autocomplete.query'
      text: string
      misspelled: boolean
      match: number
      operator: 'and' | 'or'
      locale: string
      session?: string
      anonymous?: string
    }

const sendEvent = (options: SearchEvent & { url?: string }) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const userAgent = window?.navigator?.userAgent ?? ''

  const { accountName } = getStoreConfig()

  axiosClient
    .post(
      `https://sp.vtex.com/event-api/v1/${accountName}/event`,
      {
        ...options,
        userAgent,
        anonymous: user.anonymous(),
        session: user.session(),
      },
      {
        headers: {
          'content-type': 'application/json',
        },
      }
    )
    .then(() => console.warn('Intelligent Search Event Sent'))
    .catch((err) => {
      console.warn(err)
    })
}

export const sendSearchClickEvent = (event: SearchClickEvent) => {
  const url = new URL(event.params.url)

  if (event.name === 'search_select_item') {
    sendEvent({
      type: 'search.click',
      productId: event.params.productId,
      position: event.params.position,
      url: url.href,
      text: url.searchParams.get('q') ?? '<empty>',
    })
  }
}

export const sendSearchQueryEvent = (event: SearchEventQuery) => {
  const url = new URL(event.params.url)

  if (event.name === 'intelligent_search_query') {
    sendEvent({
      type: 'search.query',
      url: url.href,
      text: url.searchParams.get('q') ?? '<empty>',
      misspelled: false,
      match: event.params.results,
      operator: 'and',
      locale: 'pt-BR',
    })
  }
}

export const sendAutoCompleteEvent = (event: SearchAutocomplete) => {
  const url = new URL(event.params.url)

  if (event.name === 'search_autocomplete') {
    sendEvent({
      type: 'search.autocomplete.query',
      url: url.href,
      text: event.params.text ?? '<empty>',
      misspelled: event.params.misspelled,
      match: event.params.results,
      operator: 'and',
      locale: 'pt-BR',
    })
  }
}

setInterval(
  () => sendEvent({ type: 'session.ping' }),
  60 * 1e3 /* One minute */
)
